// Task type definitions
//
// NB: `status` è ora nullable per rappresentare i task inbox (senza progetto).
// `project_detail`/`status_detail`/`location` arrivano dal backend (BE 6.0/6.3)
// e sono i campi autorevoli per la collocazione di un task. I vecchi consumer
// che leggono `task.status?.key` continuano a funzionare perché status resta
// definito per i task di progetto.
// I tipi dominio completi (TaskProject, TaskSummary, TaskScope, MoveTaskPayload,
// ecc.) sono in `src/types/tasks.ts`.
import type {
  TaskProjectSummary,
  TaskStatusRef,
  TaskLocation,
} from "../types/tasks";

export type TaskStatus = string; // Dynamic status from backend (e.g., "DA_FARE", "IN_CORSO", etc.)
export type TaskPriority = "ALTA" | "MEDIA" | "BASSA";

export interface TaskAssignee {
  id: number;
  username: string;
  email?: string;
}

/**
 * Dettaglio colonna. Estende TaskStatusRef (BE 6.0) aggiungendo `key` obbligatorio
 * per i consumer legacy, ma i campi project-scoped sono opzionali per compatibilità
 * con i payload vecchi. Usare sempre `project_id` autorevole quando presente.
 */
export interface TaskStatusDetail {
  id: number;
  key: string;
  name: string;
  color: string;
  is_default: boolean;
  is_active: boolean;
  order: number;
  // Campi project-scoped (BE TaskStatusRef). Assenti nei payload legacy.
  project_id?: number;
  project_name?: string;
}

export interface TaskAttachment {
  id: number;
  filename: string;
  file: string;
  uploaded_by: string;
  uploaded_at: string;
  file_size?: number;
}

export interface Task {
  id: number;
  title: string;
  description?: string;
  /** Nullable per task inbox (senza progetto). */
  status: TaskStatusDetail | null;
  priority: TaskPriority;
  assigned_to?: TaskAssignee[];
  assigned_to_name?: string;
  created_by?: string;
  due_date?: string;
  attachments?: TaskAttachment[];
  done?: boolean;
  is_completed?: boolean;
  order?: number;
  category?: string;
  created_at?: string;
  updated_at?: string;
  // --- Campi progetto/collocazione (BE 6.3) ---
  /** PK progetto (write field). Null per inbox. */
  project?: number | null;
  /** Location letta dal backend: "project" | "inbox". */
  location?: TaskLocation;
  /** Riferimento al progetto (read field autosufficiente). Null per inbox. */
  project_detail?: TaskProjectSummary | null;
  /** Riferimento alla colonna (read field autosufficiente, BE TaskStatusRef). */
  status_detail?: TaskStatusRef | null;
  /** Posizione nella colonna (usata da dnd-kit e move). */
  position?: number;
}

/**
 * Type guard: un task è inbox quando manca del progetto (o status).
 * Da preferire a controlli sparsi su `task.project === null`.
 */
export function isInboxTask(task: Pick<Task, "project"> | undefined | null): boolean {
  return task == null || task.project === null || task.project === undefined;
}

/**
 * Type guard: status valido e attivo (utile nei filtri e nelle card).
 */
export function isActiveStatus(status: TaskStatusDetail | null | undefined): status is TaskStatusDetail {
  return !!status && status.is_active !== false;
}

/**
 * Validazione runtime di una colonna proveniente dal backend.
 *
 * Il frontend fa molto affidamento su ``id`` e ``key`` (React key, stato menu,
 * ID dnd-kit). Un ``as TaskStatusDetail[]`` cast non aggiunge i campi mancanti
 * a runtime: questo guard espone eventuali regressioni del contratto API invece
 * di mascherarle con ``undefined === undefined``.
 */
export function isValidTaskStatusDetail(value: unknown): value is TaskStatusDetail {
  if (!value || typeof value !== "object") return false;
  const col = value as Record<string, unknown>;
  return (
    Number.isInteger(col.id) &&
    typeof col.key === "string" &&
    col.key.length > 0 &&
    typeof col.name === "string"
  );
}

export type { TaskProjectSummary, TaskStatusRef, TaskLocation };

// Priority display maps (from legacy code)
export const PRIORITY_DISPLAY_MAP: Record<number, TaskPriority> = {
  1: 'BASSA',
  2: 'MEDIA',
  3: 'ALTA'
};

export const PRIORITY_API_MAP: Record<TaskPriority, number> = {
  'BASSA': 1,
  'MEDIA': 2,
  'ALTA': 3
};

/**
 * Get priority badge styles for UI display
 */
export function getPriorityBadgeStyles(priority: TaskPriority) {
  const map = {
    ALTA: {
      bg: "bg-[#FFD4D4] dark:bg-[rgba(255,100,103,0.35)]",
      text: "text-[#dc2626] dark:text-[#ff6467]",
      border: "border-[#FFB0B0] dark:border-[rgba(255,100,103,0.45)]",
      label: "Urgente",
    },
    MEDIA: {
      bg: "bg-[#FFE4B5] dark:bg-[rgba(254,154,0,0.35)]",
      text: "text-[#d97706] dark:text-[#fe9a00]",
      border: "border-[#FFD080] dark:border-[rgba(254,154,0,0.45)]",
      label: "Medio",
    },
    BASSA: {
      bg: "bg-[#D4F5D4] dark:bg-[rgba(34,197,94,0.2)]",
      text: "text-[#16a34a] dark:text-[#22c55e]",
      border: "border-[#A8E6A8] dark:border-[rgba(34,197,94,0.3)]",
      label: "Bassa",
    },
  };
  return map[priority];
}

/**
 * Restituisce il colore accent (hex) della priorità, usato ad esempio
 * per il bordino laterale della card compatta nel Kanban.
 * Allineato con i colori di testo di getPriorityBadgeStyles (variante light).
 */
export function getPriorityAccentColor(priority: TaskPriority): string {
  const map: Record<TaskPriority, string> = {
    ALTA:  "#dc2626",  // rosso — stesso di getPriorityBadgeStyles.text (light)
    MEDIA: "#d97706",  // ambra
    BASSA: "#16a34a",  // verde
  };
  return map[priority] ?? "#6b7280"; // fallback grigio
}

/**
 * Get status label for display
 */
export function getStatusLabel(status: TaskStatusDetail | string): string {
  if (typeof status === 'string') {
    return status;
  }
  return status.name;
}

/**
 * Get assignee gradient for avatar
 */
export function getAssigneeGradient(userId: number): string {
  // Generate consistent gradient based on user ID
  const variants = [
    "bg-gradient-to-br from-[#9f8e2c] to-[#d9cb2d]",
    "bg-gradient-to-br from-[#068768] to-[#05df72]",
    "bg-gradient-to-br from-[#994d97] to-[#e8d4fd]",
    "bg-gradient-to-br from-[#2b7fff] to-[#4d9eff]",
    "bg-gradient-to-br from-[#ff6467] to-[#ff8487]",
  ];
  return variants[userId % variants.length];
}

/**
 * Get initials from username
 */
export function getUserInitials(username: string): string {
  return username
    .split(" ")
    .map(n => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Pure function: update a task by ID with a partial patch
 */
export function updateTaskById(tasks: Task[], taskId: number, patch: Partial<Task>): Task[] {
  return tasks.map((t) => (t.id === taskId ? { ...t, ...patch } : t));
}

/**
 * Pure function: move a task to a new status
 */
export function moveTaskToStatus(tasks: Task[], taskId: number, toStatus: TaskStatusDetail): Task[] {
  return tasks.map((t) => (t.id === taskId ? { ...t, status: toStatus } : t));
}

/**
 * Generate session key for temporary attachments
 */
export function generateSessionKey(): string {
  return 'task_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

/**
 * Format date for API (YYYY-MM-DD)
 */
export function formatDateForApi(date: string | Date): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formato breve "21 apr" (giorno + mese abbreviato italiano) per visualizzazione
 * compatta (es. card Kanban in modalità compact).
 * Se la data è null/undefined restituisce "—".
 */
export function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "—";
    // toLocaleDateString('it-IT', {day, month:'short'}) produce "21 apr." — togliamo il punto finale per pulizia
    const s = d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    return s.replace(/\.$/, "");
  } catch {
    return "—";
  }
}

/**
 * Generate key from status name
 */
export function generateKey(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}
